import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileText, Upload, Undo2, CheckCircle2, Image as ImageIcon, CreditCard, Barcode, AlertCircle, X } from "lucide-react";
import { toast } from "sonner";
import { FotoZoom } from "./FotoGrande";
import { RevisionDialog } from "./RevisionDialog";
import { Pill, toB64, fotoUrl } from "./util";

const Barra = ({ icon: I, t }) => <div className="flex items-center gap-2 rounded-lg bg-violet-100 px-3 py-1.5 text-sm font-black text-violet-900"><I className="w-4 h-4" />{t}</div>;

// Miniatura (25% del tamaño anterior); clic para ampliar.
const Mini = ({ src, testid, onQuitar }) => {
  const [z, setZ] = useState(false);
  return (
    <div className="relative w-28 h-24 sm:w-36 sm:h-28 rounded-lg border bg-zinc-50 overflow-hidden" data-testid={testid}>
      <img src={fotoUrl(src)} alt="" onClick={() => setZ(true)} className="w-full h-full object-cover cursor-zoom-in" />
      {onQuitar && <button onClick={onQuitar} className="absolute top-1 right-1 rounded-full bg-red-600 text-white p-0.5" data-testid={`${testid}-quitar`}><X className="w-3 h-3" /></button>}
      {z && <FotoZoom src={src} onClose={() => setZ(false)} />}
    </div>
  );
};

const PagoBanco = ({ p, i, d, bloqueado, put, tid }) => {
  const [ref, setRef] = useState(d.referencia);
  const fileRef = useRef(null);
  const subir = async (files) => put(p.id, { fotos_base64: await Promise.all([...files].map(toB64)) });
  return (
    <div className="space-y-2" data-testid={`pago-req-${tid}-${i}`}>
      <div className="text-xs font-bold">Pago {i + 1} · {p.caja} · Q{p.monto.toFixed(2)}{bloqueado && <span className="text-emerald-700"> · facturado</span>}</div>
      <Barra icon={CreditCard} t={`Créditos bancarios (${d.creditos.length})`} />
      <div className="flex flex-wrap gap-2">{d.creditos.map((f, j) => <Mini key={f} src={f} testid={`cred-${tid}-${i}-${j}`} onQuitar={bloqueado ? null : () => put(p.id, { quitar: f })} />)}</div>
      {!bloqueado && <>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files?.length && subir(e.target.files)} data-testid={`cred-file-${tid}-${i}`} />
        <button onClick={() => fileRef.current?.click()} className="w-full rounded-lg border-2 border-dashed border-violet-300 py-2 text-sm flex items-center justify-center gap-2 hover:bg-violet-50" data-testid={`cred-subir-${tid}-${i}`}><Upload className="w-4 h-4" />Subir foto(s) del crédito bancario</button>
      </>}
      <Barra icon={Barcode} t="Número de referencia bancaria" />
      <div className="flex gap-2">
        <Input value={ref} disabled={bloqueado} onChange={(e) => setRef(e.target.value)} placeholder="N° de referencia bancaria" data-testid={`ref-${tid}-${i}`} />
        {!bloqueado && <Button variant="outline" onClick={() => put(p.id, { referencia: ref })} disabled={ref === d.referencia} data-testid={`ref-guardar-${tid}-${i}`}>Guardar</Button>}
      </div>
    </div>
  );
};

export const CobroDetalle = ({ r, fecha, onChanged }) => {
  const [rev, setRev] = useState(false);
  const tid = r.numero;
  const hechos = r.md.facturados || [];
  const fotos = r.pagos.flatMap((p) => p.fotos);
  const bancos = r.pagos.filter((p) => p.tipo !== "efectivo");
  const puedeFacturar = r.md.pendientes > 0 && r.md.faltan.length === 0;
  const put = async (pago_id, body) => {
    try { await api.put("/mi-dinero/ciudad/credito", { order_id: r.id, fecha, pago_id, ...body }); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const facturar = async () => {
    await api.post(`/mi-dinero/ciudad/${r.id}/facturar`, { fecha });
    toast.success(`Pedido ${r.numero} facturado`); setRev(false); onChanged();
  };
  const deshacer = async () => {
    const motivo = window.prompt("Motivo para deshacer la facturación.\nSe anularán las facturas en el sistema. Si la factura ya fue emitida ante la SAT, también debe hacer su anulación fiscal.");
    if (!motivo) return;
    try { await api.post(`/mi-dinero/ciudad/${r.id}/deshacer`, { fecha, motivo }); toast.success("Facturación anulada"); onChanged(); } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="m-2 rounded-2xl border border-violet-200 p-3 grid gap-4 md:grid-cols-2 md:divide-x" data-testid={`cobro-detalle-${tid}`}>
      <div className="space-y-2">
        <Barra icon={ImageIcon} t={`1. Comprobantes del repartidor (${fotos.length})`} />
        {fotos.length ? <div className="flex flex-wrap gap-2">{fotos.map((f, i) => <Mini key={f} src={f} testid={`comp-${tid}-${i}`} />)}</div>
          : <div className="text-xs text-red-600 font-bold">El repartidor no subió comprobantes.</div>}
      </div>
      <div className="space-y-3 md:pl-4">
        {bancos.length === 0 && <div className="text-xs text-zinc-600">Pago en efectivo: no requiere crédito bancario ni referencia.</div>}
        {bancos.map((p) => <PagoBanco key={p.id} p={p} i={r.pagos.indexOf(p)} d={r.md.pagos[p.id] || { creditos: [], referencia: "" }} bloqueado={hechos.includes(p.id)} put={put} tid={tid} />)}
        {r.md.faltan.length > 0 && <ul className="rounded-lg bg-red-50 border border-red-200 p-2 text-xs font-bold text-red-700 space-y-0.5" data-testid={`faltan-${tid}`}>
          {r.md.faltan.map((f) => <li key={f} className="flex gap-1"><AlertCircle className="w-3.5 h-3.5 shrink-0" />{f}</li>)}</ul>}
        {r.md.pendientes > 0 && <Button onClick={() => setRev(true)} disabled={!puedeFacturar} className="w-full h-12 text-lg bg-emerald-600 hover:bg-emerald-700 font-black" data-testid={`md-facturar-${tid}`}><FileText className="w-5 h-5 mr-2" />FACTURAR</Button>}
        {hechos.length > 0 && <div className="flex flex-wrap items-center gap-2">
          <Pill tone="green" testid={`facturado-${tid}`}><CheckCircle2 className="w-3 h-3" />FACTURADO</Pill>
          {r.md.facturas.map((f) => <span key={f.numero} className="text-xs"><b>{f.numero}</b> · {f.banco} Q{f.monto.toFixed(2)}</span>)}
          <Button variant="outline" size="sm" onClick={deshacer} data-testid={`deshacer-${tid}`}><Undo2 className="w-4 h-4 mr-1" />Deshacer facturación</Button>
        </div>}
        {r.md.historial.length > 0 && <div className="text-[11px] text-zinc-500" data-testid={`hist-${tid}`}>{r.md.historial.map((h, i) => <div key={i}>{new Date(h.at).toLocaleString("es-GT", { timeZone: "America/Guatemala" })} · {h.accion.replace("_", " ")} · {h.nombre || h.por}{h.motivo ? ` · ${h.motivo}` : ""}</div>)}</div>}
      </div>
      {rev && <RevisionDialog url={`/mi-dinero/ciudad/${r.id}/revision`} params={{ fecha }} onConfirm={facturar} onClose={() => setRev(false)} />}
    </div>
  );
};
